/**
 * ERPs Sync Policy
 *
 * Fetches connectors first, then subscribes to ExternalSystemsIntegrations
 * and filters for ERP systems. This eliminates the race condition where
 * the subscription could fire before connectors were available.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType, ReadmodelChange } from '@bett3r-dev/teselly-client-library';
import { SystemIntegrationConnectorReadmodel, SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
import { ERPsState } from '../state';
import { ERPsCommand } from '../commands';

/**
 * Creates a policy that syncs ERPs from backend subscription
 */
export const createERPsSyncPolicy = (
  backend: ClientLibraryType
): Policy<ERPsState, ERPsCommand> => ({
  name: 'erps-sync',

  init: ({ dispatch }) => {
    let subscription: { close: () => void } | null = null;
    let cancelled = false;

    dispatch(
      { type: 'integrations/erps/loading/setErps', payload: { isLoading: true }},
      { source: 'api' }
    );

    backend.queries.SystemsIntegrationsConnectors({}).then(( connectors ) => {
      if ( cancelled ) return;

      const connectorsMap = connectors.reduce<Record<string, SystemIntegrationConnectorReadmodel>>(
        ( acc, connector ) => {
          acc[connector.id] = connector as SystemIntegrationConnectorReadmodel;
          return acc;
        },
        {}
      );

      subscription = backend.subscriptions.ExternalSystemsIntegrations(
        {},
        ( change: ReadmodelChange<SystemIntegrationsReadmodel> ) => {
          if ( change.type === 'snapshot' ) {
            const erpItems = change.data.items.filter( item => {
              const connector = connectorsMap[item.connectorId];
              return connector && connector.type !== 'salesChannel';
            });
            const erpsRecord = erpItems.reduce<Record<string, SystemIntegrationsReadmodel>>(
              ( acc, erp ) => {
                acc[erp.id] = erp;
                return acc;
              },
              {}
            );
            dispatch(
              { type: 'integrations/erps/data/setAll', payload: { erps: erpsRecord }},
              { source: 'api' }
            );
            return;
          }

          if ( change.type === 'signal' ) return;

          const record = change.type === 'delete' ? change.previous : change.data;
          const connector = connectorsMap[record.connectorId];
          if ( !connector || connector.type === 'salesChannel' ) return;

          if ( change.type === 'delete' ) {
            dispatch(
              { type: 'integrations/erps/data/remove', payload: { erpId: change.previous.id }},
              { source: 'api' }
            );
          } else {
            dispatch(
              { type: 'integrations/erps/data/upsert', payload: { erp: change.data }},
              { source: 'api' }
            );
          }
        }
      );

      setTimeout(() => {
        if ( !cancelled ) {
          dispatch(
            { type: 'integrations/erps/loading/setErps', payload: { isLoading: false }},
            { source: 'api' }
          );
        }
      }, 500 );
    });

    return () => {
      cancelled = true;
      subscription?.close();
    };
  }
});
